import React, { useState } from "react";
import { Stack, Textarea, Button, Typography } from "@mui/joy";

const MAX_LENGTH = 500;
const COUNTER_THRESHOLD = 400;

interface CommentInputProps {
  activityId: string;
  onSubmit: (content: string) => Promise<void>;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  activityId,
  onSubmit,
}) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const trimmed = content.trim();
  const isDisabled = trimmed.length === 0 || submitting;

  const handleSubmit = async () => {
    if (isDisabled) return;
    setSubmitting(true);
    try {
      await onSubmit(content);
      setContent("");
    } catch {
      // Error handled by store
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end", mt: 1 }}>
      <Stack sx={{ flex: 1, position: "relative" }}>
        <Textarea
          data-testid={`comment-input-${activityId}`}
          placeholder="Add a comment..."
          value={content}
          onChange={(e) => {
            if (e.target.value.length <= MAX_LENGTH) {
              setContent(e.target.value);
            }
          }}
          onKeyDown={handleKeyDown}
          minRows={1}
          maxRows={3}
          sx={{
            fontSize: "13px",
            "--Textarea-focusedThickness": "1px",
          }}
        />
        {content.length >= COUNTER_THRESHOLD && (
          <Typography
            level="body-xs"
            sx={{
              position: "absolute",
              right: 8,
              bottom: 4,
              color: content.length >= MAX_LENGTH ? "danger.500" : "#aaa",
              fontSize: "10px",
            }}
          >
            {content.length}/{MAX_LENGTH}
          </Typography>
        )}
      </Stack>
      <Button
        data-testid={`comment-submit-${activityId}`}
        size="sm"
        variant="plain"
        disabled={isDisabled}
        loading={submitting}
        onClick={handleSubmit}
        sx={{ minWidth: "auto", fontSize: "13px", fontWeight: 600 }}
      >
        Post
      </Button>
    </Stack>
  );
};
